// ─────────────────────────────────────────────────────────────────────────────
// FtueLayerComponent
// ─────────────────────────────────────────────────────────────────────────────
// The single renderer of the FTUE framework. Mounted once in the app shell, it
// turns the presenter's decision into a positioned, non-blocking card:
//
//   • resolves the tip's semantic anchor, retrying while the target mounts,
//   • rings that anchor and pulses a halo behind it while the card is open, so
//     the card and the thing it talks about read as one (see
//     `styles/ui/_ftue.scss`),
//   • abandons the tip when the anchor never appears, or leaves mid-card —
//     leaving it eligible, instead of burning a "you'll only see this once" on
//     a card nobody saw,
//   • re-measures on scroll and resize (CDK reposition strategy),
//   • treats click-outside, Esc and the card's own controls as a dismissal.
//
// One overlay handles both anchored and centred tips: the centred case (a
// Settings preview of a tip that lives on another page) is positioned against
// an invisible origin pinned to the middle of the viewport.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { ConnectedPosition, OverlayModule, ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FtueAnchorRegistryService } from 'src/app/core/ftue/ftue-anchor-registry.service';
import { FtueAnchorKey } from 'src/app/core/ftue/ftue-anchors';
import { FtuePresenterService } from 'src/app/core/ftue/ftue-presenter.service';
import { FtueDismissReason, FtuePlacement, IFtuePresentation } from 'src/app/core/ftue/ftue.models';
import { CoachMarkComponent } from '../coach-mark/coach-mark.component';

/** Gap between the anchor and the card, in px. */
const ANCHOR_GAP = 12;

/**
 * Marks the element a tip points at. Styled globally, because the anchor belongs
 * to whichever feature declared it and this component cannot reach into it.
 */
const ANCHOR_HIGHLIGHT_CLASS = 'ftue-anchor-highlight';

/**
 * How long a tip waits for its anchor before giving up.
 *
 * Generous on purpose. A fixed handful of attempts (the first version tried six
 * over ~0.7s) silently abandoned the tip whenever the page was slower than that
 * — a toolbar behind a permission check, a list still fetching, a cold dev
 * build. The symptom was a tip that "sometimes doesn't fire", which is worse
 * than one that never does.
 *
 * Nothing keeps a timer alive past the page view: leaving the page clears the
 * presentation, and `registered$` short-circuits the wait the moment the anchor
 * mounts, so the retry loop only covers "registered but not yet laid out".
 */
const ANCHOR_MAX_WAIT_MS = 10_000;
const ANCHOR_RETRY_MS = 120;

/**
 * A preview waits a fraction of that. The user just clicked Preview and is
 * looking at the screen: if the anchor is not on this page, the card has to
 * centre itself now, not after ten seconds of nothing.
 */
const PREVIEW_ANCHOR_MAX_WAIT_MS = 400;

/** Where the card's secondary action goes. */
const TIPS_SETTINGS_ROUTE = '/settings/tips';

@Component({
  selector: 'ui-ftue-layer',
  standalone: true,
  imports: [NgIf, OverlayModule, CoachMarkComponent],
  templateUrl: './ftue-layer.component.html',
  styleUrls: ['./ftue-layer.component.scss'],
})
export class FtueLayerComponent implements OnInit, OnDestroy {

  /** Invisible origin used by centred tips. Always in the template. */
  @ViewChild('viewportCentre', { static: true }) viewportCentre!: ElementRef<HTMLElement>;

  presentation: IFtuePresentation | null = null;

  /**
   * What the card is positioned against. Never null — the CDK directive keeps a
   * reference to it even while closed — so an unanchored state points at the
   * viewport-centre stub and `isPositioned` decides whether anything shows.
   */
  origin!: ElementRef<HTMLElement>;

  /** Keeps the card glued to its anchor while the page behind it scrolls. */
  readonly scrollStrategy: ScrollStrategy;

  private readonly destroy$ = new Subject<void>();
  private highlighted?: HTMLElement;
  /** Whether `highlight` set a radius that `clearHighlight` has to take back. */
  private borrowedRadius = false;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private waitingSince = 0;
  private isPositioned = false;
  private isCentred = false;

  constructor(
    private readonly presenter: FtuePresenterService,
    private readonly anchors: FtueAnchorRegistryService,
    private readonly router: Router,
    scrollStrategyOptions: ScrollStrategyOptions,
  ) {
    this.scrollStrategy = scrollStrategyOptions.reposition({ autoClose: true });
  }

  ngOnInit(): void {
    this.origin = this.viewportCentre;

    this.presenter.presentation$
      .pipe(takeUntil(this.destroy$))
      .subscribe((presentation) => this.onPresentationChange(presentation));

    // An anchor that mounts late (a dialog, a lazily rendered panel) should not
    // have to wait for the next retry tick.
    this.anchors.registered$
      .pipe(takeUntil(this.destroy$))
      .subscribe((key) => {
        if (this.presentation && !this.isPositioned && this.presentation.tip.anchor === key) {
          this.tryResolveAnchor();
        }
      });

    // The anchor left the DOM under an open card: a dialog closed, or the
    // condition resolved itself (the missing-company chip is replaced by the
    // company the user just picked). Navigation is not involved, so nothing else
    // would notice.
    this.anchors.unregistered$
      .pipe(takeUntil(this.destroy$))
      .subscribe((key) => this.onAnchorLeft(key));
  }

  ngOnDestroy(): void {
    this.clearRetry();
    this.clearHighlight();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isOpen(): boolean {
    return !!this.presentation && this.isPositioned;
  }

  get positions(): ConnectedPosition[] {
    return POSITIONS[this.currentPlacement];
  }

  // ── Dismissal ──────────────────────────────────────────────────────────────

  onDismiss(reason: FtueDismissReason): void {
    this.presenter.dismiss(reason);
  }

  /**
   * Dismissed first, then navigated: the user engaged with this card, so it is
   * read. Leaving it to the router's own clean-up would keep it eligible and
   * show it again later.
   */
  onSettingsRequested(): void {
    this.presenter.dismiss('action');
    this.router.navigateByUrl(TIPS_SETTINGS_ROUTE);
  }

  /**
   * Esc closes the card wherever focus is. The overlay is non-blocking, so it
   * usually does not hold focus and `(overlayKeydown)` would never fire.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.presenter.dismiss('escape');
    }
  }

  /**
   * The CDK detached the overlay on its own — the anchor scrolled out of reach.
   * The user never engaged with the card, so the tip is abandoned, not seen.
   */
  onDetach(): void {
    if (this.presentation) {
      this.presenter.abandon();
    }
  }

  // ── Anchoring ──────────────────────────────────────────────────────────────

  private onPresentationChange(presentation: IFtuePresentation | null): void {
    this.clearRetry();
    this.clearHighlight();
    this.presentation = presentation;
    this.isPositioned = false;
    this.isCentred = false;

    if (!presentation) {
      return;
    }
    if (!presentation.tip.anchor) {
      this.centreCard();
      return;
    }
    this.waitingSince = Date.now();
    this.tryResolveAnchor();
  }

  private tryResolveAnchor(): void {
    const presentation = this.presentation;
    if (!presentation?.tip.anchor) {
      return;
    }

    const element = this.anchors.resolve(presentation.tip.anchor);
    if (element) {
      this.clearRetry();
      this.origin = new ElementRef(element);
      this.isCentred = false;
      this.isPositioned = true;
      this.highlight(element);
      return;
    }

    const patience = presentation.isPreview ? PREVIEW_ANCHOR_MAX_WAIT_MS : ANCHOR_MAX_WAIT_MS;
    if (Date.now() - this.waitingSince < patience) {
      this.clearRetry();
      this.retryTimer = setTimeout(() => this.tryResolveAnchor(), ANCHOR_RETRY_MS);
      return;
    }

    // Out of patience. A preview was asked for explicitly, so fall back to the
    // centre of the viewport rather than doing nothing; a triggered tip is
    // abandoned and stays eligible for a page where its anchor does exist.
    this.clearRetry();
    if (presentation.isPreview) {
      this.centreCard();
    } else {
      this.presenter.abandon();
    }
  }

  /**
   * One of this key's elements is gone. If another is still on screen — the same
   * concept rendered on two surfaces — the card follows it; otherwise the tip is
   * abandoned, so it stays eligible for a page where its subject exists.
   *
   * A preview falls back to the centre of the viewport instead: it was asked for
   * explicitly, and it records nothing either way.
   */
  private onAnchorLeft(key: FtueAnchorKey): void {
    const presentation = this.presentation;
    if (!presentation || !this.isPositioned || presentation.tip.anchor !== key) {
      return;
    }

    const replacement = this.anchors.resolve(key);
    if (replacement) {
      this.origin = new ElementRef(replacement);
      this.highlight(replacement);
      return;
    }

    this.clearHighlight();
    if (presentation.isPreview) {
      this.centreCard();
    } else {
      this.presenter.abandon();
    }
  }

  /**
   * Rings the anchor. Only ever one element at a time.
   *
   * The ring is a `box-shadow`, so it follows the element's own corners — right
   * when the anchor is the visible control, wrong when the anchor is a component
   * host wrapping one. `<ui-table-column-settings>` is square; the rounded
   * button lives inside it, so the ring drew a hard box around a rounded control.
   *
   * The radius is borrowed rather than the ring being moved to the child: the
   * element carrying the anchor key is the element that should light up, which
   * is also what the e2e asserts by reading `data-ftue` off the ringed node.
   */
  private highlight(element: HTMLElement): void {
    this.clearHighlight();

    const borrowed = borrowedRadius(element);
    if (borrowed) {
      element.style.borderRadius = borrowed;
      this.borrowedRadius = true;
    }

    element.classList.add(ANCHOR_HIGHLIGHT_CLASS);
    this.highlighted = element;
  }

  private clearHighlight(): void {
    // Only ever take back what was added here: an anchor with corners of its own
    // was left untouched, and clearing those would flatten the real control.
    if (this.highlighted && this.borrowedRadius) {
      this.highlighted.style.removeProperty('border-radius');
    }
    this.borrowedRadius = false;
    this.highlighted?.classList.remove(ANCHOR_HIGHLIGHT_CLASS);
    this.highlighted = undefined;
  }

  private centreCard(): void {
    this.origin = this.viewportCentre;
    this.isCentred = true;
    this.isPositioned = true;
  }

  private get currentPlacement(): FtuePlacement {
    if (!this.presentation || this.isCentred) {
      return 'center';
    }
    return this.presentation.tip.placement;
  }

  private clearRetry(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
  }
}

// ── Position tables ──────────────────────────────────────────────────────────
// Each placement lists its preferred position first and its flipped fallbacks
// after, so a card near a viewport edge lands on the other side of the anchor
// instead of being pushed over it.
const POSITIONS: Record<FtuePlacement, ConnectedPosition[]> = {
  bottom: [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: ANCHOR_GAP },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: ANCHOR_GAP },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -ANCHOR_GAP },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -ANCHOR_GAP },
  ],
  top: [
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -ANCHOR_GAP },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -ANCHOR_GAP },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: ANCHOR_GAP },
  ],
  right: [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: ANCHOR_GAP },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -ANCHOR_GAP },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: ANCHOR_GAP },
  ],
  left: [
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -ANCHOR_GAP },
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: ANCHOR_GAP },
    { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: -ANCHOR_GAP },
  ],
  center: [
    { originX: 'center', originY: 'center', overlayX: 'center', overlayY: 'center' },
  ],
};

// ── Corner inheritance ───────────────────────────────────────────────────────

/**
 * How much of the anchor a child has to cover before its corners are treated as
 * the anchor's own. A small rounded badge inside a large square container must
 * not lend its radius to the whole box.
 */
const FILLS_ANCHOR = 0.9;

/** True when every corner is zero — including the empty string a stub can return. */
function isSquare(radius: string): boolean {
  return !radius || /^(0(px|%)?\s*)+$/.test(radius.trim());
}

/**
 * The radius the ring should use, or `undefined` to leave the element alone.
 *
 * An anchor with corners of its own already rings correctly, so nothing is
 * returned for it. Only a square anchor goes looking for a rounded child that
 * fills it — the component-host case, where the control the user sees is one
 * level in and the host is just a positioning box.
 *
 * Direct children only: one level covers a wrapper around a control, and a
 * deeper walk would start finding corners that belong to something smaller.
 */
function borrowedRadius(element: HTMLElement): string | undefined {
  if (!isSquare(getComputedStyle(element).borderRadius)) {
    return undefined;
  }

  const box = element.getBoundingClientRect();
  if (!box.width || !box.height) {
    return undefined;
  }

  for (const child of Array.from(element.children)) {
    const radius = getComputedStyle(child).borderRadius;
    if (isSquare(radius)) {
      continue;
    }
    const childBox = child.getBoundingClientRect();
    if (
      childBox.width >= box.width * FILLS_ANCHOR &&
      childBox.height >= box.height * FILLS_ANCHOR
    ) {
      return radius;
    }
  }

  return undefined;
}
